import { useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Copy, Palette, Ratio, Layout, Unlink, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import { useSharedImage, useMySharedImages, useUnshareImage } from "@/hooks/useShare"
import { useStyles } from "@/hooks/useStyles"
import { useTemplate } from "@/hooks/useTemplates"
import { useGeneratorStore } from "@/stores/generatorStore"
import { localizeName } from "@/lib/i18n-content"
import { TemplatePreviewModal } from "@/features/templates/components/TemplatePreviewModal"
import { AiGeneratedNotice } from "@/components/common/AiGeneratedNotice"
import type { SharedImage } from "@/types/share"
import type { ApplyableSettings } from "@/types/generation"
import { restoreTemplateParameters } from "@/features/templates/lib/restore-template-parameters"

interface CommunityPreviewModalProps {
  image: SharedImage | null
  onClose: () => void
}

export function CommunityPreviewModal({ image, onClose }: CommunityPreviewModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: sharedDetail } = useSharedImage(image?.id ?? null)
  const { data: styles } = useStyles()
  const applySettings = useGeneratorStore((state) => state.applySettings)

  const { data: mySharedImages } = useMySharedImages()
  const unshareMutation = useUnshareImage()

  const [showUnshareConfirm, setShowUnshareConfirm] = useState(false)

  // 템플릿 상세 모달
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const { data: selectedTemplate } = useTemplate(selectedTemplateId)

  const getStyleName = (styleId: string | null): string | null => {
    if (!styleId) return null
    const style = styles?.find((s) => s.id === styleId)
    return style ? localizeName(style) : styleId
  }

  const myShareId = image ? mySharedImages?.get(image.s3Key) : undefined

  const handleUnshareConfirm = async () => {
    if (!myShareId) return

    try {
      await unshareMutation.mutateAsync(myShareId)
      toast.success(t('community.unshareSuccess'))
      setShowUnshareConfirm(false)
      onClose()
    } catch {
      toast.error(t('community.unshareFailed'))
    }
  }

  const handleUseSettings = async () => {
    if (!sharedDetail) return

    const settings: ApplyableSettings = {
      prompt: sharedDetail.generation.prompt,
      style: sharedDetail.generation.style ?? undefined,
      aspectRatio: sharedDetail.generation.aspectRatio as ApplyableSettings['aspectRatio'],
      template: sharedDetail.generation.template ?? undefined,
    }

    applySettings(await restoreTemplateParameters(settings))
    onClose()
    navigate('/my')
    toast.success(t('community.settingsAppliedToastTitle'), {
      description: t('community.settingsAppliedToastDescription'),
    })
  }

  if (!image) return null

  return (
    <Dialog open={!!image} onOpenChange={(open) => { if (!open) { setShowUnshareConfirm(false); onClose() } }}>
      <DialogContent aria-describedby={undefined} className="w-full h-full max-w-none max-h-none sm:max-w-4xl sm:max-h-[90vh] sm:h-auto p-0 gap-0 overflow-y-auto overscroll-contain rounded-none sm:rounded-lg inset-0 sm:inset-auto sm:top-[50%] sm:left-[50%] translate-x-0 translate-y-0 sm:translate-x-[-50%] sm:translate-y-[-50%]">
        <DialogTitle className="sr-only">{t('community.sharedImageDetail')}</DialogTitle>
        <div className="flex flex-col sm:flex-row min-h-0">
          {/* 좌측: 이미지 */}
          <div className="flex-shrink-0 sm:flex-1 bg-slate-100 flex flex-col items-center justify-center gap-3 p-4 sm:min-h-[500px]">
            <img
              src={image.imageUrl}
              alt={image.prompt}
              className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
            />
            <AiGeneratedNotice />
          </div>

          {/* 우측: 정보 패널 */}
          <div className="flex-shrink-0 w-full sm:w-80 bg-white p-6 flex flex-col">
            {/* 프롬프트 */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-500 mb-2">{t('community.prompt')}</h3>
              <div className="max-h-[300px] overflow-y-auto p-3 bg-slate-50 rounded-lg">
                <p className="text-slate-800 text-sm leading-relaxed whitespace-pre-wrap">
                  {sharedDetail?.generation.prompt ?? image.prompt}
                </p>
              </div>
            </div>

            {/* 메타 정보 */}
            <div className="space-y-3 mb-6">
              {/* 스타일 */}
              {(sharedDetail?.generation.style || image.style) && (
                <div className="flex items-center gap-2 text-sm">
                  <Palette className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">{t('community.styleLabel')}</span>
                  <span className="text-slate-800 font-medium">
                    {getStyleName(sharedDetail?.generation.style ?? image.style)}
                  </span>
                </div>
              )}

              {/* 비율 */}
              <div className="flex items-center gap-2 text-sm">
                <Ratio className="w-4 h-4 text-slate-400" />
                <span className="text-slate-600">{t('community.ratioLabel')}</span>
                <span className="text-slate-800 font-medium">
                  {sharedDetail?.aspectRatio ?? image.aspectRatio}
                </span>
              </div>

              {/* 템플릿 */}
              {sharedDetail?.generation.template && (
                <div className="flex items-center gap-2 text-sm">
                  <Layout className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">{t('community.templateLabel')}</span>
                  <button
                    onClick={() => setSelectedTemplateId(sharedDetail.generation.template!.id)}
                    className="text-slate-800 font-medium hover:text-primary hover:underline transition-colors"
                  >
                    {localizeName(sharedDetail.generation.template)}
                  </button>
                </div>
              )}
            </div>

            {/* 버튼 */}
            <div className="mt-auto pt-4 border-t border-slate-100 space-y-2">
              {showUnshareConfirm ? (
                <div className="space-y-2">
                  <p className="text-sm text-slate-600 text-center">
                    {t('community.unshareConfirmTitle')}
                    <br />
                    <span className="text-slate-400">{t('community.unshareConfirmDescription')}</span>
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowUnshareConfirm(false)}
                      className="flex-1"
                      disabled={unshareMutation.isPending}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleUnshareConfirm}
                      className="flex-1"
                      disabled={unshareMutation.isPending}
                    >
                      {unshareMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('community.processing')}
                        </>
                      ) : (
                        t('common.confirm')
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <Button
                    onClick={handleUseSettings}
                    className="w-full"
                    disabled={!sharedDetail}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    {t('community.useSettings')}
                  </Button>
                  {myShareId && (
                    <Button
                      variant="outline"
                      onClick={() => setShowUnshareConfirm(true)}
                      className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Unlink className="w-4 h-4 mr-2" />
                      {t('community.unshare')}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </DialogContent>

      {/* 템플릿 상세 모달 */}
      <TemplatePreviewModal
        template={selectedTemplate ?? null}
        isOpen={!!selectedTemplateId && !!selectedTemplate}
        onClose={() => setSelectedTemplateId(null)}
      />
    </Dialog>
  )
}
